import React, { useState } from 'react';
import styled from 'styled-components';
import { BookOpen, ChevronRight, ChevronDown, Package, Truck, Clock, MapPin, Phone, CreditCard, RotateCcw, HelpCircle } from 'lucide-react';
import ContentLayout from '../../../Common/Component/ContentLayout';

const Container = styled.div`
  width: 100%;
`;

const QuickGuide = styled.div`
  background: linear-gradient(135deg, #3b82f6 0%, #1e40af 100%);
  color: white;
  padding: 30px;
  border-radius: 12px;
  margin-bottom: 30px;
`;

const QuickTitle = styled.h2`
  font-size: 24px;
  font-weight: 700;
  margin: 0 0 15px 0;
  text-align: center;
`;

const QuickDescription = styled.p`
  font-size: 16px;
  opacity: 0.9;
  text-align: center;
  margin: 0 0 25px 0;
  line-height: 1.5;
`;

const QuickLinks = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 15px;
`;

const QuickLink = styled.a`
  background: rgba(255, 255, 255, 0.2);
  padding: 15px;
  border-radius: 8px;
  text-decoration: none;
  color: white;
  display: flex;
  align-items: center;
  gap: 10px;
  font-weight: 500;
  transition: all 0.2s;
  
  &:hover {
    background: rgba(255, 255, 255, 0.3);
    transform: translateY(-2px);
  }
`;

const GuideSection = styled.div`
  background: white;
  border-radius: 12px;
  box-shadow: 0 4px 6px rgba(0,0,0,0.1);
  margin-bottom: 20px;
  overflow: hidden;
`;

const SectionHeader = styled.div`
  background: #f9fafb;
  padding: 20px;
  border-bottom: 1px solid #e5e7eb;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: space-between;
  transition: background 0.2s;
  
  &:hover {
    background: #f3f4f6;
  }
`;

const SectionTitle = styled.h3`
  font-size: 18px;
  font-weight: 600;
  color: #1f2937;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 10px;
`;

const SectionContent = styled.div`
  padding: 25px;
  display: ${props => props.isOpen ? 'block' : 'none'};
`;

const GuideStep = styled.div`
  display: flex;
  gap: 15px;
  margin-bottom: 20px;
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const StepNumber = styled.div`
  width: 32px;
  height: 32px;
  background: #3b82f6;
  color: white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  font-size: 14px;
  flex-shrink: 0;
`;

const StepContent = styled.div`
  flex: 1;
`;

const StepTitle = styled.h4`
  font-size: 16px;
  font-weight: 600;
  color: #1f2937;
  margin: 0 0 8px 0;
`;

const StepDescription = styled.p`
  font-size: 14px;
  color: #6b7280;
  line-height: 1.5;
  margin: 0;
`;

const InfoBox = styled.div`
  background: #f0f9ff;
  border: 1px solid #0ea5e9;
  border-radius: 8px;
  padding: 15px;
  margin: 15px 0;
`;

const InfoTitle = styled.h5`
  font-size: 14px;
  font-weight: 600;
  color: #0c4a6e;
  margin: 0 0 8px 0;
  display: flex;
  align-items: center;
  gap: 6px;
`;

const InfoText = styled.p`
  font-size: 13px;
  color: #075985;
  margin: 0;
  line-height: 1.4;
`;

const FAQSection = styled.div`
  background: white;
  border-radius: 12px;
  box-shadow: 0 4px 6px rgba(0,0,0,0.1);
`;

const FAQHeader = styled.div`
  background: #1f2937;
  color: white;
  padding: 25px;
  text-align: center;
`;

const FAQTitle = styled.h3`
  font-size: 20px;
  font-weight: 600;
  margin: 0 0 8px 0;
`;

const FAQSubtitle = styled.p`
  opacity: 0.8;
  margin: 0;
`;

const FAQList = styled.div`
  padding: 20px;
`;

const FAQItem = styled.div`
  border-bottom: 1px solid #f3f4f6;
  
  &:last-child {
    border-bottom: none;
  }
`;

const FAQQuestion = styled.div`
  padding: 15px 0;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-weight: 500;
  color: #1f2937;
  
  &:hover {
    color: #3b82f6;
  }
`;

const FAQAnswer = styled.div`
  padding: 0 0 15px 0;
  color: #6b7280;
  font-size: 14px;
  line-height: 1.5;
  display: ${props => props.isOpen ? 'block' : 'none'};
`;

const ContactSection = styled.div`
  background: #fef3c7;
  border: 1px solid #f59e0b;
  border-radius: 12px;
  padding: 25px;
  text-align: center;
  margin-top: 20px;
`;

const ContactTitle = styled.h3`
  font-size: 18px;
  font-weight: 600;
  color: #d97706;
  margin: 0 0 10px 0;
`;

const ContactInfo = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 20px;
  margin-top: 15px;
`;

const ContactItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  color: #92400e;
  font-weight: 500;
`;

function DeliveryGuide() {
  const [openSections, setOpenSections] = useState({
    tracking: true,
    delivery: false,
    returns: false,
    general: false
  });

  const [openFAQs, setOpenFAQs] = useState({});

  const toggleSection = (section) => {
    setOpenSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const toggleFAQ = (index) => {
    setOpenFAQs(prev => ({
      ...prev,
      [index]: !prev[index]
    }));
  };

  const guideData = {
    tracking: {
      title: '배송 추적 방법',
      icon: <Package size={20} />,
      steps: [
        {
          title: '운송장 번호 확인',
          description: '주문 완료 후 발송된 이메일이나 SMS에서 운송장 번호를 확인하세요.'
        },
        {
          title: '배송추적 페이지 접속',
          description: '상단 메뉴의 "배송추적"을 클릭하거나 직접 배송추적 페이지로 이동하세요.'
        },
        {
          title: '운송장 번호 입력',
          description: '확인한 운송장 번호를 검색창에 입력하고 "조회하기"를 클릭하세요.'
        },
        {
          title: '배송현황 확인',
          description: '실시간 배송 현황과 예상 배송 완료 시간을 확인할 수 있습니다.'
        }
      ],
      info: {
        title: '알아두세요!',
        content: '운송장 번호는 상품 발송 후 1-2시간 내에 조회 가능하며, 실시간으로 업데이트됩니다.'
      }
    },
    delivery: {
      title: '배송 정보',
      icon: <Truck size={20} />,
      steps: [
        {
          title: '배송 시간',
          description: '평일 오전 9시부터 오후 6시까지 배송되며, 주말 및 공휴일은 배송이 제한될 수 있습니다.'
        },
        {
          title: '배송 지역',
          description: '전국 어디든 배송 가능하며, 도서산간 지역은 추가 배송일이 소요될 수 있습니다.'
        },
        {
          title: '배송 방법',
          description: 'CJ대한통운, 한진택배, 로젠택배 등 신뢰할 수 있는 택배사를 통해 배송됩니다.'
        },
        {
          title: '부재중 배송',
          description: '부재시에는 택배함 보관 또는 재배송을 진행하며, 기사와 직접 연락하여 재배송 일정을 조율할 수 있습니다.'
        }
      ],
      info: {
        title: '배송비 안내',
        content: '3만원 이상 구매시 무료배송이며, 3만원 미만 구매시 배송비 3,000원이 추가됩니다.'
      }
    },
    returns: {
      title: '반품/교환 절차',
      icon: <RotateCcw size={20} />,
      steps: [
        {
          title: '반품/교환 신청',
          description: '마이페이지에서 해당 주문의 반품/교환을 신청하거나 고객센터로 연락하세요.'
        },
        {
          title: '상품 검수',
          description: '반품/교환 사유를 확인하고 상품 상태를 검수한 후 승인 여부를 결정합니다.'
        },
        {
          title: '상품 수거',
          description: '승인 후 택배기사가 방문하여 상품을 수거하거나, 직접 발송할 수 있습니다.'
        },
        {
          title: '처리 완료',
          description: '반품의 경우 환불 처리되고, 교환의 경우 새 상품이 발송됩니다.'
        }
      ],
      info: {
        title: '반품/교환 기간',
        content: '상품 수령 후 7일 이내에 신청 가능하며, 상품의 상태에 따라 제한될 수 있습니다.'
      }
    },
    general: {
      title: '일반 배송 정보',
      icon: <MapPin size={20} />,
      steps: [
        {
          title: '주문 처리 시간',
          description: '주문 완료 후 1-2일 이내에 상품이 발송됩니다. (주말, 공휴일 제외)'
        },
        {
          title: '배송 알림',
          description: 'SMS와 이메일로 발송, 배송중, 배송완료 알림을 받을 수 있습니다.'
        },
        {
          title: '배송 주소 변경',
          description: '상품 발송 전에는 주소 변경이 가능하며, 발송 후에는 택배사를 통해 변경해야 합니다.'
        },
        {
          title: '안전한 배송',
          description: '상품 파손 방지를 위해 안전 포장하며, 파손 시 즉시 교환해드립니다.'
        }
      ],
      info: {
        title: '특별 배송 서비스',
        content: '새벽배송, 당일배송 등 특별 배송 서비스는 지역 및 상품에 따라 제한될 수 있습니다.'
      }
    }
  };

  const faqData = [
    {
      question: '운송장 번호가 조회되지 않아요',
      answer: '상품 발송 후 1-2시간 정도 후에 조회가 가능합니다. 그래도 조회되지 않으면 고객센터로 문의해주세요.'
    },
    {
      question: '배송 예정일을 변경할 수 있나요?',
      answer: '상품 발송 전에는 주문 취소 후 재주문하시면 되고, 발송 후에는 택배기사와 직접 연락하여 배송일 조율이 가능합니다.'
    },
    {
      question: '부분 배송이 가능한가요?',
      answer: '여러 상품을 주문한 경우, 재고 상황에 따라 부분 배송될 수 있습니다. 각각 다른 운송장 번호로 배송됩니다.'
    },
    {
      question: '배송비는 얼마인가요?',
      answer: '3만원 이상 구매시 무료배송이며, 3만원 미만은 배송비 3,000원입니다. 도서산간 지역은 추가 배송비가 있을 수 있습니다.'
    },
    {
      question: '반품 배송비는 누가 부담하나요?',
      answer: '단순변심의 경우 고객 부담이며, 상품 불량이나 배송 오류의 경우 판매자가 부담합니다.'
    }
  ];

  return (
    <ContentLayout 
      title="배송 안내"
      subtitle="배송과 관련된 모든 정보를 확인하실 수 있습니다"
    >
      <Container>
        <QuickGuide>
          <QuickTitle>빠른 배송 서비스</QuickTitle>
          <QuickDescription>
            AntsMarket의 신속하고 안전한 배송 서비스를 경험해보세요
          </QuickDescription>
          <QuickLinks>
            <QuickLink href="/delivery-tracking">
              <Package size={20} />
              배송 추적
            </QuickLink>
            <QuickLink href="/delivery-tracking/status">
              <MapPin size={20} />
              실시간 추적
            </QuickLink>
            <QuickLink href="/delivery-tracking/history">
              <Clock size={20} />
              배송 내역
            </QuickLink>
            <QuickLink href="/support">
              <Phone size={20} />
              고객센터
            </QuickLink>
          </QuickLinks>
        </QuickGuide>

        {Object.entries(guideData).map(([key, section]) => (
          <GuideSection key={key}>
            <SectionHeader onClick={() => toggleSection(key)}>
              <SectionTitle>
                {section.icon}
                {section.title}
              </SectionTitle>
              {openSections[key] ? <ChevronDown size={20} /> : <ChevronRight size={20} />}
            </SectionHeader>
            <SectionContent isOpen={openSections[key]}>
              {section.steps.map((step, index) => (
                <GuideStep key={index}>
                  <StepNumber>{index + 1}</StepNumber>
                  <StepContent>
                    <StepTitle>{step.title}</StepTitle>
                    <StepDescription>{step.description}</StepDescription>
                  </StepContent>
                </GuideStep>
              ))}
              <InfoBox>
                <InfoTitle>
                  <HelpCircle size={14} />
                  {section.info.title}
                </InfoTitle>
                <InfoText>{section.info.content}</InfoText>
              </InfoBox>
            </SectionContent>
          </GuideSection>
        ))}

        <FAQSection>
          <FAQHeader>
            <FAQTitle>자주 묻는 질문</FAQTitle>
            <FAQSubtitle>배송과 관련하여 자주 문의하시는 내용들입니다</FAQSubtitle>
          </FAQHeader>
          <FAQList>
            {faqData.map((faq, index) => (
              <FAQItem key={index}>
                <FAQQuestion onClick={() => toggleFAQ(index)}>
                  Q. {faq.question}
                  {openFAQs[index] ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                </FAQQuestion>
                <FAQAnswer isOpen={openFAQs[index]}>
                  A. {faq.answer}
                </FAQAnswer>
              </FAQItem>
            ))}
          </FAQList>
        </FAQSection>

        <ContactSection>
          <ContactTitle>더 자세한 도움이 필요하신가요?</ContactTitle>
          <p style={{margin: '0 0 15px 0', color: '#92400e'}}>
            고객센터를 통해 언제든 문의해주세요
          </p>
          <ContactInfo>
            <ContactItem>
              <Phone size={18} />
              1588-1234
            </ContactItem>
            <ContactItem>
              <Clock size={18} />
              평일 09:00-18:00
            </ContactItem>
          </ContactInfo>
        </ContactSection>
      </Container>
    </ContentLayout>
  );
}

export default DeliveryGuide;